import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeStyle } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MediaItem } from '../../app.models';
import { CarouselComponent } from 'ngx-bootstrap/carousel';
import { ASSET_BASE_URL } from '../../app.data.service';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
})
export class CardComponent implements OnInit, OnChanges {
  BASE_MEDIA_URL = ASSET_BASE_URL;
  @ViewChild('cardCarousel', { static: false }) protected carousel: CarouselComponent;

  @Input() id: string;

  @Input() imageUrl: string;
  @Input() mediaItems: MediaItem[];

  @Input() classes = 'background-dark general-dark2-bg';
  @Input() paddingClasses = 'content-padding no-gutter';

  @Input() mobileMode = true;

  @Input() showTopImage = true;
  @Input() topImageLink = null;
  @Input() topImageLinkText = null;
  @Input() topImageLinkClasses = 'general-white';

  @Input() topLeft: string;
  @Input() topLeftLink: string;
  @Input() topRight: string;

  @Input() topLeftClasses = 'cream-alternate';
  @Input() topLeftTextClasses = 'cream-alternate';
  @Input() topRightClasses = 'cream-alternate';
  @Input() topRightTextClasses = 'cream-alternate';

  @Input() title: string;
  @Input() titleClasses = 'h4 cream-light';

  @Input() subTitle: string;
  @Input() subtitleClasses = 'cream-alternate';

  @Input() description: string;
  @Input() descriptionClasses = 'cream-alternate';
  @Input() descriptionRowClasses = 'pb-3';
  @Input() limitDescription = true;

  @Input() bottomLeft: string;
  @Input() bottomRight: string;
  @Input() bottomLeftClasses = 'cream-alternate';
  @Input() bottomRightClasses = 'gray2';
  @Input() bottomRightTextClasses = 'gray2';

  @Input() showBorder = true;

  @Input() buttonText: string;
  @Input() buttonRoute: string;
  @Input() buttonClasses = 'btn-primary1';

  @Input() fullCardClick = true;
  @Input() linkText: string;
  @Input() linkRoute: string;
  @Input() linkAboveDescription = false;
  @Input() linkClasses = 'primary1-light';

  safeStyleUrlCache = {};
  safeResourceUrlCache = {};
  autoplayVideo = false;
  fieldCache = {};

  constructor(private sanitize: DomSanitizer,
              private router: Router) {

  }

  ngOnInit() {
    // console.log('DEBUG: showTopImage', this.imageUrl, this.showTopImage);
    // console.log('fullCardClick', this.fullCardClick);
    this.updateSafeUrls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateSafeUrls();
    this.fieldCache = {};
    // console.log('CardComponent(): have carousel', this.carousel);
  }

  safeStyle(url: string): SafeStyle {
    if (!!this.safeStyleUrlCache[url]) {
      // console.log('safeStyle(): cached');
      return this.safeStyleUrlCache[url];
    }
    // console.log('safeStyle()');
    this.safeStyleUrlCache[url] = this.sanitize.bypassSecurityTrustStyle('url(' + url + ')');
    return this.safeStyleUrlCache[url];
  }

  safeHtml(fieldName: string, html: string): SafeHtml {
    if (this.fieldCache[fieldName]) {
      return this.fieldCache[fieldName];
    }

    if (!!html && typeof html === 'string') {
      html = html.replace(/<a /g, `<a class="${this.linkClasses}" `);
      html = html.replace(/href=(["'])([^'"]+)(["'])/g, (match, $1, $2, offset, original) => `href="${$2}" target="_blank" onclick="window.open('${$2}', '_blank'); return true;"`);
    } else if (typeof html === 'string') {
      return null;
    }

    const safeContent = this.sanitize.bypassSecurityTrustHtml(html);
    this.fieldCache[fieldName] = safeContent;
    return safeContent;
  }

  isRemoteUrl(url: string) {
    return url != null && (!!url.match(/^https?:/) || !!url.match(/^\/\//));
  }

  safeResourceUrl(url: string, autoplayVideo?: boolean, embed?: boolean): SafeResourceUrl {
    if (!url) {
      return;
    }

    let fullUrl = url;
    let haveParams = fullUrl.indexOf('?') >= 0;
    if (autoplayVideo === true) {
      if (!haveParams) {
        fullUrl += '?autoplay=1';
      } else {
        fullUrl += '&autoplay=1';
      }
      haveParams = true;
    }

    // if (!!this.safeResourceUrlCache[fullUrl]) {
    //   // console.log('safeResourceUrl(): cached');
    //   // console.log('returning cached URL', url);
    //   return this.safeResourceUrlCache[fullUrl];
    // }

    // console.log('safeResourceUrl()');

    if (embed === true) {
      let extra = !haveParams ? '?enablejsapi=1' : '&enablejsapi=1';
      extra += `&origin=${location.href}`;
      fullUrl = location.protocol + fullUrl + extra;
    }

    // console.log('loading URL', fullUrl);

    // this.safeResourceUrlCache[url] = this.sanitize.bypassSecurityTrustResourceUrl(fullUrl);
    // return this.safeResourceUrlCache[url];
    return this.sanitize.bypassSecurityTrustResourceUrl(fullUrl);
  }

  updateSafeUrls() {
    // console.log('updateSafeUrls()');
    // update all media urls
    if (!!this.mediaItems) {
      for (const media of this.mediaItems) {
        if (!!media.embedUrl) {
          this.safeResourceUrlCache[media.embedUrl] = {};
          this.safeResourceUrlCache[media.embedUrl][true] = this.safeResourceUrl(media.embedUrl, true, true);
          this.safeResourceUrlCache[media.embedUrl][false] = this.safeResourceUrl(media.embedUrl, false, true);
          // console.log('embedUrl w/autoplay', this.safeResourceUrlCache[media.embedUrl][true]);
          // console.log('embedUrl wo/autoplay', this.safeResourceUrlCache[media.embedUrl][false]);
        }
        if (!!media.imageUrl) {
          this.safeResourceUrlCache[media.imageUrl] = this.safeResourceUrl(media.imageUrl);
        }
        if (!!media.videoUrl) {
          this.safeResourceUrlCache[media.videoUrl] = this.safeResourceUrl(media.videoUrl);
        }
      }
    }
  }

  playVideo() {
  //console.log('CardComponent(): playing video');
    this.carousel.selectSlide(this.carousel.slides.length - 1);
    this.autoplayVideo = true;
    this.updateSafeUrls();
  }

  goto(href, event?: Event) {
  //console.log('URL clicked', href);
    if (!!event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!!href.match(/^\/\//)) {
      href = location.protocol + href;
    }
  //console.log('navigating to', href);
    window.open(href, '_blank');
    return true;
  }

  gotoRoute(route, event?: Event) {
  //console.log('route clicked', route);
    if (!!event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.router.navigateByUrl(route);
    return true;
  }

  cardClicked(event: Event) {
    if (this.fullCardClick) {
      if (!!this.linkRoute) {
      //console.log('full card click enabled, redirecting', this.linkRoute);
        if (!!this.linkRoute && !this.isRemoteUrl(this.linkRoute)) {
          if (this.linkRoute.startsWith('/media/')) {
          //console.log('prepending base media url', this.linkRoute);
            this.goto(this.BASE_MEDIA_URL + this.linkRoute);
          } else {
            this.router.navigateByUrl(this.linkRoute);
          }
        } else if (!!this.linkRoute && this.isRemoteUrl(this.linkRoute)) {
          this.goto(this.linkRoute);
        }
      } else {
      //console.log('full card click enabled, but no link, falling through');
      }
    } else {
    //console.log('full card click NOT enabled, NOT redirecting');
      event.preventDefault();
      // event.stopPropagation();
      return true;
    }
  }
}
